import "./ChatInput.scss";
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useLocation} from "react-router";
import {Box, Skeleton, Stack, Typography} from "@mui/material";
import {observer} from "mobx-react-lite";
import {useChatContext} from "../../context/ChatContext";
import {AgentName} from "~/pages/chat-agent/components/AgentName/AgentName";
import {ChatGreeting} from "./ChatGreeting";
import {AgentTemplates} from "../AgentTemplates/AgentTemplates";
import {formatPromptWithSlashCommands, promptContainsPlaceholders} from "../../utils/promptPlaceholders";
import {
    clearChatDraft,
    getChatDraftKey,
    NEW_CHAT_DRAFT_KEY,
    readChatDraft,
    writeChatDraft,
} from "../../utils/chatDraftStorage";
import {ChatLexicalEditor, type ChatLexicalInputHandle} from "./ChatLexicalEditor";
import {useAgentMetadata} from "../../hooks/useAgentMetadata";
import {showsAgentTemplates, useChatSurface} from "../../hooks/useChatSurface";
import {useStores} from "../../../../stores";
import {agentLabel} from "~/pages/chat-agent/utils/toolLabels";
import {SendButton} from "~/pages/chat-agent/components/ChatInput/SendButton";
import {StopButton} from "~/pages/chat-agent/components/ChatInput/StopButton";

export type {ChatLexicalInputHandle};

export type ChatInputProps = {
    onSubmit: (value: string) => void | Promise<void>;
    /** Focus and content API for the Lexical editor surface. */
    inputRef?: React.RefObject<ChatLexicalInputHandle | null>;
    disabled?: boolean;
    /** After the first messages exist, the input uses fewer rows. */
    conversationStarted?: boolean;
    defaultPosition?: "start" | "end" | "center";
    /** False when the surrounding chat shows the empty-state greeting itself (docked panels). */
    showGreeting?: boolean;
    /** Home dashboard: always use the new-chat draft slot, not the active session. */
    newChatDraftOnly?: boolean;
    /** When false, skips session-switch / restore skeletons (e.g. home dashboard). */
    showChatSwitchLoading?: boolean;
    /** The agent is answering: the placeholder says so, and with `onStop` a Stop button replaces Send. */
    working?: boolean;
    onStop?: () => void;
};

const DEFAULT_INPUT_PLACEHOLDER = "Ask anything about your spend data…";
const REQUEST_INPUT_PLACEHOLDER = "Type your answer…";

export const ChatInput: React.FC<ChatInputProps> = observer(({
                                                                 onSubmit,
                                                                 inputRef,
                                                                 disabled,
                                                                 conversationStarted = false,
                                                                 defaultPosition,
                                                                 showGreeting = true,
                                                                 newChatDraftOnly = false,
                                                                 showChatSwitchLoading = true,
                                                                 working = false,
                                                                 onStop,
                                                             }) => {
    const {selectedAppName, currentSession, isRestoringSession, pendingInput} =
        useChatContext();
    const {bagStore} = useStores();
    const location = useLocation();
    const surface = useChatSurface();
    const bagId = bagStore.bagId;
    const appName = currentSession?.appName ?? selectedAppName;
    const draftKey = newChatDraftOnly
        ? NEW_CHAT_DRAFT_KEY
        : getChatDraftKey(currentSession?.id);
    const {data: agentsMetadata, isPending: isAgentsMetadataPending} =
        useAgentMetadata();
    const [isDraftLoading, setIsDraftLoading] = useState(false);
    const isInputLoading =
        isAgentsMetadataPending ||
        (showChatSwitchLoading && (isRestoringSession || isDraftLoading));
    const inputDisabled = Boolean(disabled || isInputLoading);

    const placeholder = useMemo(() => {
        if (working) {
            return `${agentLabel(appName, agentsMetadata)} is working…`;
        }
        // The home box always starts a new chat, so it never answers a paused question.
        if (pendingInput && !newChatDraftOnly) {
            return REQUEST_INPUT_PLACEHOLDER;
        }
        if (!agentsMetadata?.length) {
            return DEFAULT_INPUT_PLACEHOLDER;
        }

        const match = agentsMetadata.find((a) => a.app_name === appName);
        const fromMeta = match?.input_placeholder?.trim();
        return fromMeta || DEFAULT_INPUT_PLACEHOLDER;
    }, [agentsMetadata, appName, pendingInput, working, newChatDraftOnly]);

    const maxRows = conversationStarted ? 1 : 2;
    const fallbackRef = useRef<ChatLexicalInputHandle | null>(null);
    const editorRef = inputRef ?? fallbackRef;
    const [contentTrimmedLen, setContentTrimmedLen] = useState(0);
    const contentTrimmedLenRef = useRef(0);
    const position = conversationStarted ? "end" : defaultPosition || "center";
    const activeDraftKeyRef = useRef<string | null>(null);
    const activeBagIdRef = useRef<number | undefined>(undefined);
    const activeAppNameRef = useRef<string | null>(null);
    /** While a send is in flight, force-clear on persist (Lexical clear / session id races). */
    const suppressDraftPersistRef = useRef(false);

    const setTrimmedLen = useCallback((len: number) => {
        contentTrimmedLenRef.current = len;
        setContentTrimmedLen(len);
    }, []);

    const persistDraftNow = useCallback(
        (
            targetBagId: number,
            targetAppName: string,
            targetDraftKey: string,
            options?: {persistEmpty?: boolean},
        ) => {
            if (suppressDraftPersistRef.current) {
                writeChatDraft(targetBagId, targetAppName, targetDraftKey, "");
                return;
            }
            const text = editorRef.current?.getText() ?? "";
            const persistEmpty = options?.persistEmpty ?? false;
            // Skip empty writes only when the editor was torn down while it still
            // had content (unmount race). If the user cleared the input, trimmed
            // length is 0 and we must remove the stored draft.
            if (!text.trim() && !persistEmpty && contentTrimmedLenRef.current > 0) {
                return;
            }
            writeChatDraft(targetBagId, targetAppName, targetDraftKey, text);
        },
        [editorRef],
    );

    const loadDraftIntoEditor = useCallback(
        (
            targetBagId: number | undefined,
            targetAppName: string,
            targetDraftKey: string,
        ) => {
            if (!targetBagId) {
                editorRef.current?.clear();
                setTrimmedLen(0);
                return;
            }
            const saved = readChatDraft(targetBagId, targetAppName, targetDraftKey);
            if (saved) {
                editorRef.current?.restoreDraftText(saved);
                setTrimmedLen(saved.trim().length);
            } else {
                editorRef.current?.clear();
                setTrimmedLen(0);
            }
        },
        [editorRef, setTrimmedLen],
    );

    const persistActiveDraft = useCallback(() => {
        const key = activeDraftKeyRef.current;
        const id = activeBagIdRef.current;
        const activeAppName = activeAppNameRef.current;
        if (key !== null && id !== undefined && activeAppName !== null) {
            persistDraftNow(id, activeAppName, key);
        }
    }, [persistDraftNow]);

    useEffect(() => {
        const prevKey = activeDraftKeyRef.current;
        const prevBagId = activeBagIdRef.current;
        const prevAppName = activeAppNameRef.current;

        const bagChanged = prevBagId !== undefined && prevBagId !== bagId;
        const keyChanged = prevKey !== null && prevKey !== draftKey;
        const appChanged = prevAppName !== null && prevAppName !== appName;
        const needsDraftSwitch =
            prevKey === null ||
            prevKey !== draftKey ||
            prevBagId !== bagId ||
            prevAppName !== appName;

        if (
            !needsDraftSwitch &&
            !(
                prevKey !== null &&
                prevBagId !== undefined &&
                prevAppName !== null &&
                (bagChanged || keyChanged || appChanged)
            )
        ) {
            return;
        }

        if (showChatSwitchLoading) {
            setIsDraftLoading(true);
        }

        if (
            prevKey !== null &&
            prevBagId !== undefined &&
            prevAppName !== null &&
            (bagChanged || keyChanged || appChanged)
        ) {
            persistDraftNow(prevBagId, prevAppName, prevKey, {persistEmpty: true});
        }
        if (needsDraftSwitch) {
            loadDraftIntoEditor(bagId, appName, draftKey);
            activeDraftKeyRef.current = draftKey;
            activeBagIdRef.current = bagId;
            activeAppNameRef.current = appName;
        }

        if (showChatSwitchLoading) {
            void Promise.resolve().then(() => setIsDraftLoading(false));
        }
    }, [
        draftKey,
        bagId,
        appName,
        persistDraftNow,
        loadDraftIntoEditor,
        showChatSwitchLoading,
    ]);

    useLayoutEffect(() => {
        const handlePageHide = () => persistActiveDraft();
        window.addEventListener("pagehide", handlePageHide);
        return () => {
            window.removeEventListener("pagehide", handlePageHide);
            persistActiveDraft();
        };
    }, [persistActiveDraft]);

    // Home dashboard: re-read __new__ after chat page unmount has persisted (mount order race).
    useEffect(() => {
        if (!newChatDraftOnly || !bagId) {
            return;
        }
        const syncNewChatDraftFromStorage = () => {
            loadDraftIntoEditor(bagId, appName, NEW_CHAT_DRAFT_KEY);
            activeDraftKeyRef.current = NEW_CHAT_DRAFT_KEY;
            activeBagIdRef.current = bagId;
            activeAppNameRef.current = appName;
        };
        queueMicrotask(syncNewChatDraftFromStorage);
    }, [location.key, bagId, appName, newChatDraftOnly, loadDraftIntoEditor]);

    const handleTextChange = useCallback((trimmedLength: number) => {
        if (trimmedLength > 0) {
            suppressDraftPersistRef.current = false;
        }
        setTrimmedLen(trimmedLength);
    }, [setTrimmedLen]);

    async function handleSubmit() {
        if (inputDisabled) {
            return;
        }
        const raw = editorRef.current?.getText() ?? "";
        const v = raw.trim();
        if (!v) {
            return;
        }
        const draft = raw;
        const submittedDraftKey = draftKey;
        const submittedAppName = appName;
        const clearNewChatSlot =
            newChatDraftOnly ||
            submittedDraftKey === NEW_CHAT_DRAFT_KEY ||
            submittedDraftKey === "temp";
        // Lexical clear is async; suppress persist so session/key switches and
        // unmount cannot write the sent text back into localStorage.
        suppressDraftPersistRef.current = true;
        editorRef.current?.clear();
        setTrimmedLen(0);
        if (bagId) {
            clearChatDraft(bagId, submittedAppName, submittedDraftKey);
            if (clearNewChatSlot) {
                clearChatDraft(bagId, submittedAppName, NEW_CHAT_DRAFT_KEY);
            }
        }
        try {
            await Promise.resolve(onSubmit(v));
            // After send (+ agent response when onSubmit awaits streaming), keep LS clear.
            if (bagId) {
                clearChatDraft(bagId, submittedAppName, submittedDraftKey);
                if (clearNewChatSlot) {
                    clearChatDraft(bagId, submittedAppName, NEW_CHAT_DRAFT_KEY);
                }
                const currentKey = activeDraftKeyRef.current;
                const currentAppName = activeAppNameRef.current;
                if (
                    currentKey &&
                    currentAppName &&
                    currentKey !== submittedDraftKey &&
                    !(clearNewChatSlot && currentKey === NEW_CHAT_DRAFT_KEY)
                ) {
                    clearChatDraft(bagId, currentAppName, currentKey);
                }
            }
            suppressDraftPersistRef.current = false;
        } catch {
            suppressDraftPersistRef.current = false;
            editorRef.current?.restoreDraftText(draft);
            setTrimmedLen(draft.trim().length);
            if (bagId) {
                writeChatDraft(bagId, submittedAppName, submittedDraftKey, draft);
            }
        }
    }

    const sendDisabled = !contentTrimmedLen || inputDisabled;
    // While the agent works, Stop takes Send's place and size.
    const showStop = working && onStop != null && !isInputLoading;

    return (
        <Box>
            <Box className={`${position}-position chat-input-container`}>
                <Box>
                    {!conversationStarted && showGreeting && (
                        <ChatGreeting sx={{marginBottom: "29px"}}/>
                    )}
                    <Box
                        sx={{
                            position: "relative",
                            minWidth: 0,
                            borderRadius: "24px",
                            border: "1px solid var(--Schemes-Outline-Variant, #C4C6D0)",
                            background: "var(--Schemes-Surface-Bright, #F9F9FF)",
                            boxShadow: "0 4px 16px 0 rgba(0, 0, 0, 0.05)",
                            padding: 2,
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                minHeight: maxRows * 24,
                            }}
                        >
                            <Box
                                sx={{
                                    visibility: isInputLoading ? "hidden" : "visible",
                                    position: 'relative',
                                }}
                            >
                                {/* <Typography sx={{position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, backgroundColor: '#f9f9ff', zIndex: promptHover ? 10 : 0}}>
                                    {promptHover ? formatPromptWithSlashCommands(promptHover) : null}
                                </Typography> */}
                                <ChatLexicalEditor
                                    ref={editorRef}
                                    placeholder={
                                        isInputLoading ? (
                                            <Skeleton variant="text" width={"20vw"} height={32}/>
                                        ) : (
                                            placeholder
                                        )
                                    }
                                    disabled={inputDisabled}
                                    maxRows={maxRows}
                                    onTextChange={handleTextChange}
                                    onEnterSubmit={() => void handleSubmit()}
                                    position={
                                        position === "end"
                                            ? {placement: "top-start"}
                                            : {placement: "bottom-start"}
                                    }
                                />
                            </Box>
                        </Box>
                        {showStop && onStop ? (
                            <StopButton onClick={onStop}/>
                        ) : (
                            <SendButton
                                disabled={sendDisabled}
                                loading={isInputLoading}
                                onClick={() => void handleSubmit()}
                            />
                        )}
                        <Stack
                            direction="row"
                            gap="8px"
                            justifyContent={"space-between"}
                            alignItems={"center"}
                        >
                            {isInputLoading ? (
                                <Skeleton variant="text" width={'25vw'} height={28}/>
                            ) : (
                                <Tip/>
                            )}
                            {isInputLoading ? (
                                <Skeleton variant="rounded" width={110} height={28}/>
                            ) : (
                                <AgentName appName={appName}/>
                            )}
                        </Stack>
                    </Box>
                </Box>
                {!conversationStarted && !isInputLoading && showsAgentTemplates(surface) && (
                    <AgentTemplates
                        appName={selectedAppName}
                        onPromptHover={(prompt) => {
                            if (!prompt) {
                                editorRef.current?.clear();
                                return;
                            }
                            editorRef.current?.setPlainText(
                                formatPromptWithSlashCommands(prompt),
                                {highlightSlashEntities: true, selection: 'none'},
                            );
                        }}
                        onPromptSelect={(prompt) => {
                            if (promptContainsPlaceholders(prompt)) {
                                editorRef.current?.insertPromptWithEntities(prompt);
                            } else {
                                editorRef.current?.setPlainText(prompt);
                            }
                            queueMicrotask(() => editorRef.current?.focus());
                        }}
                    />
                )}
            </Box>
        </Box>
    );
});

const TIP_TEXT ="Tip: Type / to insert suppliers and materials.";

// One line only: truncates with an ellipsis next to the agent picker; the full text is in the title.
function Tip() {
    return (
        <Typography
            variant={"caption"}
            noWrap
            title={TIP_TEXT}
            sx={{display: "block", flex: 1, minWidth: 0}}
        >
            <Box component="span" fontWeight={500} mr="8px">
                Tip:
            </Box>
            <Box component="span" color="text.secondary">
                Type
                <Box
                    component="span"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        verticalAlign: "middle",
                        mx: "4px",
                        borderRadius: "4px",
                        border: "0.5px solid var(--Schemes-Outline-Variant, #C4C6D0)",
                        background: "var(--Schemes-Surface-Container-Lowest, #FFF)",
                        color: "var(--Material-Theme-key-colors-primary, #4589FF)",
                        width: "20px",
                        height: "20px",
                        fontSize: "12px",
                    }}
                >
                    /
                </Box>
                to insert suppliers and materials.
            </Box>
        </Typography>
    );
}
